import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BookingApi, GetTourRoomsParams, PutTourRoomsParams } from '@model/iceberg/service/booking';
import { TourRoomsResponse } from '@model/iceberg/deal-finder/deal-finder';
import { fetchBasket } from '@state/basket';
import { getErrorCode } from '@util/error';
import { unsetTourReferences } from '@state/slices/tours/tour-references';
import { TourActions } from '@state/slices/tours/tourActions';

export enum TourRoomsActions {
  FETCH_TOUR_ROOMS = '@TOURS/FETCH_TOUR_ROOMS',
  RECEIVE_TOUR_ROOMS_FAILURE = '@TOURS/RECEIVE_TOUR_ROOMS_FAILURE',
  SET_TOUR_ROOMS = '@TOURS/setTourRooms',
  UNSET_TOUR_ROOMS = '@TOURS/unsetTourRooms',
  PUT_TOUR_ROOMS = '@TOURS/putTourRooms',
  PUT_TOUR_ROOMS_FAILURE = '@TOURS/PUT_TOUR_ROOMS_FAILURE'
}

export interface TourRoomsState {
  hotels: Array<Array<TourRoomsResponse>> | null;
  loading: boolean;
  error: string | null;
}

export const INITIAL_TOUR_ROOMS_STATE: TourRoomsState = {
  loading: false,
  error: null,
  hotels: null
};

export const fetchTourRooms = createAsyncThunk(
  TourRoomsActions.FETCH_TOUR_ROOMS,
  async (params: GetTourRoomsParams) => {
    const api = new BookingApi();
    const response = await api.getTourRooms(params);
    return response.data;
  }
);

export const putTourRooms = createAsyncThunk(
  TourRoomsActions.PUT_TOUR_ROOMS,
  async (params: PutTourRoomsParams, { dispatch }) => {
    const bookingApi = new BookingApi();
    const { searchToken } = params;

    try {
      await bookingApi.setTourRooms(params);
      await dispatch(fetchTourRooms({ searchToken }));
      await dispatch(fetchBasket(searchToken) as any);
      return true;
    } catch (error: any) {
      throw new Error(getErrorCode(error));
    }
  }
);

const tourRoomsSlice = createSlice({
  name: TourActions.SLICE_NAME,
  initialState: INITIAL_TOUR_ROOMS_STATE,
  reducers: {
    setTourRooms: (state, action: PayloadAction<any>) => {
      let normalized: TourRoomsResponse[][] | null = null;
      if (Array.isArray(action.payload)) {
        if (Array.isArray(action.payload[0])) {
          normalized = (action.payload as any[][]).map((arr) => (Array.isArray(arr) ? arr : [arr]));
        } else {
          normalized = [action.payload];
        }
      }
      state.hotels = normalized;
      state.loading = false;
      state.error = null;
    },
    unsetTourRooms: () => {
      return INITIAL_TOUR_ROOMS_STATE;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTourRooms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTourRooms.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          if (Array.isArray(action.payload[0])) {
            state.hotels = action.payload as unknown as TourRoomsResponse[][];
          } else {
            state.hotels = [action.payload as TourRoomsResponse[]];
          }
        } else {
          state.hotels = null;
        }
        state.error = null;
      })
      .addCase(fetchTourRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tour rooms';
        state.hotels = null;
      })
      .addCase(putTourRooms.pending, (state) => {
        state.loading = true;
      })
      .addCase(putTourRooms.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(putTourRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update tour rooms';
      })
      .addCase(unsetTourReferences.type, () => {
        return INITIAL_TOUR_ROOMS_STATE;
      });
  }
});

export const { setTourRooms, unsetTourRooms, clearError } = tourRoomsSlice.actions;

export const tourRoomsReducer = tourRoomsSlice.reducer;
